import { Component } from '@angular/core';
import {
  Gateway,
  GatewayCategory,
  GatewayListingPopup,
  GatewayStatus,
  GatewayStatusLabels,
  getGatewayStatus,
  ListingGroup,
  Pager,
  ProcessorTypeLabels,
} from '../../../_models';
import { ActivatedRoute, Router } from '@angular/router';
import { GroupedListComponent } from '../../../_directives';
import { Location } from '@angular/common';
import {
  AlertService,
  CampaignService,
  CheckoutGatewayService,
  GatewayCategoryService,
  LoaderService,
} from '../../../_services';
import { forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { config } from '../../../../config/config';
import { expandCollapse } from '../../../_shared';

@Component({
  selector: 'app-checkout-gateway',
  templateUrl: './checkout-gateway.component.html',
  styleUrls: ['../grouped-listing.scss', './checkout-gateway.component.scss', '../profiles-styles.component.scss'],
  animations: [expandCollapse],
})
export class CheckoutGatewayComponent extends GroupedListComponent<Gateway, GatewayStatus> {
  public gatewayProfileStatus = GatewayStatus;
  public gatewayListingPopup = GatewayListingPopup;
  pageTitle = 'Create and manage your gateway profiles.';
  subtitle =
    'Verified gateways offer full transparency and trust signal routing to boost approval rates, while Unverified gateways ' +
    'enhance existing setups with performance-driven logic—no processor change required.';
  protected groupLabel = 'Group';
  protected ungroupedName = 'Ungrouped';
  assigningGroup: ListingGroup<Gateway> = null;
  assignGatewayId: number = null;
  assignSubmitted = false;

  gateways: Gateway[] = [];
  processorTypeLabels = ProcessorTypeLabels;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected gatewayService: CheckoutGatewayService,
    protected gatewayCategoryService: GatewayCategoryService,
    protected alertService: AlertService,
    protected loader: LoaderService,
    protected campaignService: CampaignService,
    public ngxSmartModalService: NgxSmartModalService
  ) {
    super(router, location, route, gatewayService, alertService, gatewayCategoryService, ngxSmartModalService);
    this.objectName = 'gateway';
  }

  protected initializeFilter() {
    super.initializeFilter();
    this.filter['merchant_profile__campaign'] = this.campaignService.getCurrentCampaignId();
  }

  protected queryData() {
    this.loadGroups();
  }

  protected showLoading() {
    this.loader.show();
  }

  protected hideLoading() {
    this.loader.hide();
  }

  // If categories fail to load, every gateway still lists under Ungrouped.
  // page_size stops a paged endpoint from cutting the list at its default page
  protected loadGroups() {
    const pageSize = { page_size: config.maxPageSize };
    const categories$ = this.gatewayCategoryService.list(pageSize).pipe(catchError(() => of([])));
    const gateways$ = this.gatewayService.list(Object.assign({}, this.getQueryFilter(), pageSize));

    this.loader.show();

    forkJoin([categories$, gateways$])
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (responses: [GatewayCategory[] | Pager, Gateway[] | Pager]) => {
          this.gateways = this.getRows(responses[1]);
          this.setGroups(this.getRows(responses[0]), this.gateways);
          this.loader.hide();
        },
        (error) => {
          this.handleError(error);
          this.loader.hide();
        }
      );
  }

  // List endpoints may return a plain array or a page
  protected getRows<T>(response: T[] | Pager): T[] {
    let rows: T[] = [];

    if (Array.isArray(response)) {
      rows = response;
    } else if (response && response.results) {
      rows = response.results;
    }

    return rows;
  }

  protected getItemGroupId(gateway: Gateway): number {
    return Number(gateway.category);
  }

  getItemStatus(gateway: Gateway): GatewayStatus {
    return getGatewayStatus(gateway);
  }

  // "+ Gateway" moves an existing gateway (they're created from merchant profiles); on Ungrouped it clears the category
  openAssignGateway(group: ListingGroup<Gateway>) {
    this.assigningGroup = group;
    this.assignGatewayId = null;
    this.assignSubmitted = false;
    this.ngxSmartModalService.getModal(GatewayListingPopup.AssignGateway).open();
  }

  // Archived gateways reject updates, so only live gateways outside the group can be moved in
  getAssignableGateways(): Gateway[] {
    let assignable: Gateway[] = [];

    if (this.assigningGroup) {
      assignable = this.gateways.filter(
        (gateway) => !gateway.is_deleted && this.assigningGroup.items.indexOf(gateway) === -1
      );
    }

    return assignable;
  }

  assignGateway() {
    this.assignSubmitted = true;

    if (this.assignGatewayId) {
      const group = this.assigningGroup;

      this.loader.show();
      this.gatewayService
        .patch(this.assignGatewayId, { category: this.isUngrouped(group) ? null : group.id })
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          () => {
            this.alertService.success(`Gateway moved to ${group.name}.`);
            this.closeAssignModal();
            this.loadGroups();
          },
          (error) => {
            this.handleError(error);
            this.loader.hide();
          }
        );
    }
  }

  closeAssignModal() {
    this.ngxSmartModalService.getModal(GatewayListingPopup.AssignGateway).close();
    this.assignSubmitted = false;
  }

  getGatewayCountLabel(group: ListingGroup<Gateway>): string {
    const count = this.getVisibleItems(group).length;

    return `${count} ${count === 1 ? 'Gateway' : 'Gateways'}`;
  }

  // The list API sends an empty monthly cap as the text "None", so only real numbers are formatted
  isNumber(value: string | null | undefined): boolean {
    return value !== null && value !== undefined && String(value).trim().length > 0 && !isNaN(Number(value));
  }

  navigateToGateway(id: number) {
    this.router.navigate(['/checkouts', 'gateway-profiles', 'edit', id]);
  }

  toggleGatewayStatus(event: Event, gateway: Gateway) {
    const checkbox = event.target as HTMLInputElement;
    const isActive = checkbox.checked;
    const action = isActive ? 'activate' : 'deactivate';

    const confirmed = window.confirm(`Are you sure you want to ${action} this gateway?`);

    if (confirmed) {
      const request = isActive ? this.gatewayService.enable(gateway.id) : this.gatewayService.disable(gateway.id);

      this.loader.show();
      request.pipe(takeUntil(this._destroy$)).subscribe({
        next: (updated: Gateway) => {
          gateway.is_enabled = updated.is_enabled;
          gateway.is_verified = updated.is_verified;
          gateway.is_deleted = updated.is_deleted;
          this.alertService.success('Gateway status updated successfully.');
          this.loader.hide();
        },
        error: (error) => {
          checkbox.checked = !isActive;
          this.handleError(error);
          this.loader.hide();
        },
      });
    } else {
      checkbox.checked = !isActive;
    }
  }

  // Delete archives a gateway that was onboarded to the processor and removes one that never was
  deleteGateway(gateway: Gateway) {
    gateway.showThreeDotMenu = false;

    if (window.confirm(`Are you sure you want to delete ${gateway.name}?`)) {
      this.loader.show();
      this.gatewayService
        .delete(gateway.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          () => {
            this.alertService.success('Deleted gateway.');
            this.loadGroups();
          },
          (error) => {
            this.handleError(error);
            this.loader.hide();
          }
        );
    }
  }

  getStatusLabel(gateway: Gateway): string {
    return GatewayStatusLabels[getGatewayStatus(gateway)];
  }
}
